/**
 * Got Your Number intel, derived from this seat's own pitch history.
 *
 * Every automated seat was re-deriving this by hand off `pitches[]`, and getting
 * it wrong the same two ways: ingesting a taken pitch's 0 as a chip value (0 is
 * not a legal GYN value — the sheet is 1/1/2/3), and merging both sides' values
 * into one impossible sheet.
 *
 * The second mistake is the important one. Under the two-sheet rule each side's
 * chip is valued on its OWN team's current pitcher's sheet, which is exactly why
 * every reveal carries a `*ChipValueSource` pitcher id. Attribute by that id and
 * partial knowledge compounds correctly; ignore it and the model is garbage.
 *
 * This returns INFORMATION, deliberately never a recommended chip. Two seats
 * handed the same recommendation converge on it and synchronise — a previous
 * session opened FB/FB, CH/CH, SL/SL for exactly that reason. `myThrown` and
 * `myGuesses` are included so a seat can audit its own predictability, which is
 * the half of the problem an opponent model cannot see.
 *
 * No hidden information: everything is reconstructed from pitches this seat
 * already witnessed.
 */

/** Server-side sheets are keyed by abbreviation; chips are 1-4 internally. */
export const CHIP_KEY = { FB: 1, CH: 2, CB: 3, SL: 4 };

/** A Got Your Number sheet is always a permutation of these. */
export const GYN_SHEET_VALUES = [1, 1, 2, 3];

const tally = (arr) =>
  arr.reduce((m, c) => {
    if (c == null) return m;
    m[c] = (m[c] || 0) + 1;
    return m;
  }, {});

/** half === "top" means the home team pitches and the away team bats. */
export const pitchingRoleOf = (pitch) => (pitch?.half === "top" ? "home" : "away");

/**
 * Everything known about one pitcher's sheet, plus what the {1,1,2,3} constraint
 * implies about the chips still hidden. Seeing the 3 and a 1 leaves {1,2} for the
 * other two chips — the same deduction a human makes at the table.
 */
export function sheetFor(sheets, pitcherId, chipMax = 4) {
  const known = (pitcherId && sheets[pitcherId]) || {};
  const remainingValues = [...GYN_SHEET_VALUES];
  for (const v of Object.values(known)) {
    const i = remainingValues.indexOf(v);
    if (i >= 0) remainingValues.splice(i, 1);
  }
  const unknownChips = [];
  for (let c = 1; c <= chipMax; c++) if (known[c] == null) unknownChips.push(c);
  return { known, unknownChips, remainingValues };
}

/**
 * @param pitches      this seat's pitch history, oldest first
 * @param opts.myRole  "home" | "away"
 * @param opts.chipMax highest chip type in play
 * @param opts.opponentPitcherId  the pitcher currently facing me (null unless batting)
 * @param opts.myPitcherId        my own team's current pitcher
 */
export function deriveGynIntel(pitches, opts = {}) {
  const { myRole, chipMax = 4, opponentPitcherId = null, myPitcherId = null,
          opponentPitcherName = null, chipPool = null, recentWindow = 8,
          myPitcherChipValues = null, lastKnownOpponentPitcher = null } = opts;
  if (!myRole) return null;

  // A taken pitch never ran the chip game. Its chip fields are meaningless and
  // ingesting them corrupts the sheet.
  const applied = (pitches || []).filter((p) => p && p.chipsApplied);

  const sheets = {};
  const learn = (pid, chip, val) => {
    if (!pid || chip == null || val == null || val < 1) return;
    if (!sheets[pid]) sheets[pid] = {};
    sheets[pid][chip] = val;
  };
  for (const p of applied) {
    learn(p.pitcherChipValueSource, p.pitcherChip, p.pitcherChipValue);
    learn(p.batterChipValueSource, p.batterChip, p.batterChipValue);
  }

  // Four-way split. An opponent model alone cannot tell a seat how readable it
  // has itself become, and that is what makes two scripted seats sync up.
  const opponentThrown = [], myGuesses = [], myThrown = [], opponentGuesses = [];
  for (const p of applied) {
    if (pitchingRoleOf(p) === myRole) {
      myThrown.push(p.pitcherChip);
      opponentGuesses.push(p.batterChip);
    } else {
      opponentThrown.push(p.pitcherChip);
      myGuesses.push(p.batterChip);
    }
  }
  const series = (arr) => ({ counts: tally(arr), recent: arr.slice(-recentWindow) });

  // Your OWN sheet is not learned, it is assigned — you have known it since the
  // Got Your Number modal. Seeding it from the assignment fixes it reading as
  // fully unknown right after you filled it in.
  if (myPitcherId && myPitcherChipValues) {
    const own = sheets[myPitcherId] || (sheets[myPitcherId] = {});
    for (const [chip, val] of Object.entries(myPitcherChipValues)) {
      // The server stores this sheet keyed by abbreviation ({"FB":1,...});
      // accept chip numbers too so either shape works.
      const c = CHIP_KEY[chip] ?? Number(chip);
      if (Number.isFinite(c) && val != null) own[c] = Number(val);
    }
  }

  // The opposing sheet is permanent knowledge, but `opponentPitcherId` only
  // exists while they are on the mound — so it used to blank every time the half
  // flipped, forcing every agent to cache it locally, which is exactly what the
  // playbook tells them not to do.
  const oppSheet = opponentPitcherId
    ? { id: opponentPitcherId, name: opponentPitcherName,
        ...sheetFor(sheets, opponentPitcherId, chipMax) }
    : (lastKnownOpponentPitcher
        ? { ...lastKnownOpponentPitcher, stale: true }
        : null);

  return {
    opponentPitcher: oppSheet,
    myPitcherSheet: myPitcherId ? sheetFor(sheets, myPitcherId, chipMax) : null,
    sheets,
    // `recent` is oldest-first, matching pitches[]. Weight it over `counts`: a
    // running tally keeps voting for a pattern the opponent already abandoned.
    opponentThrown: series(opponentThrown),
    opponentGuesses: series(opponentGuesses),
    myThrown: series(myThrown),
    myGuesses: series(myGuesses),
    sampleSize: applied.length,
    chipPool,
    note:
      "EV(guess c) = P(c)*max(theirValue_c, myValue_c) - sum_{d!=c} P(d)*max(theirValue_d, myValue_c). " +
      "On a MISS the pot is max(their thrown value, your chip value) and the PITCHER takes it, so a " +
      "high-value guess that misses pays your opponent — the sign of EV is set by match probability, " +
      "not by payoff. Sample rather than taking the argmax, and keep every chip above a floor (~10%): " +
      "a merely lopsided mix is nearly as exploitable as a fixed one.",
  };
}
